// DI-1 — unsaved-work protection for the whole workspace.
//
//   • Dirty tracking: a baseline fingerprint of the session snapshot is
//     taken whenever the workspace matches something persisted or freshly
//     created (mount, New, Load Session, successful save …). "Unsaved
//     work" = meaningful content that differs from that baseline.
//   • Autosave: a debounced local draft of the whole workspace while it
//     has unsaved work; the draft is cleared once the work is saved.
//   • Restore prompt: on startup a stored draft is OFFERED (never applied
//     automatically). Autosave stays off until the coach answers, so the
//     empty startup workspace can never overwrite or clear the draft.
//   • Leave-page guard: flushes the draft and asks the browser to warn,
//     only when there is unsaved work. Skipped for the intentional
//     Google sign-in redirect (the draft is flushed first).
//
// All workspace data is read through callbacks supplied by
// TrainingGround, so this hook owns no copy of the workspace itself.
import { useCallback, useEffect, useRef, useState } from "react";
import { shouldWarnBeforeDiscard, workspaceFingerprint } from "@/lib/workspaceState";
import {
  buildDraft,
  clearDraft,
  draftToWorkspace,
  isDraftWorthRestoring,
  loadDraft,
  saveDraft,
  serializeDraft,
} from "@/lib/workspaceDraft";

const AUTOSAVE_DELAY_MS = 800;

// Accessing window.localStorage itself can throw (blocked storage);
// the draft helpers already swallow errors from a null storage.
const browserStorage = () => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export function useUnsavedWorkProtection({ getSnapshot, getWorkspace, makeTabId, onRestore }) {
  // Latest callbacks, so the stable functions below always see current state.
  const latest = useRef(null);
  latest.current = { getSnapshot, getWorkspace, makeTabId, onRestore };

  const baselineRef = useRef(null);       // fingerprint of the last saved / loaded / new state
  const decidedRef = useRef(false);       // restore prompt answered, or none was needed
  const ownsDraftRef = useRef(false);     // this browser tab wrote the stored draft
  const lastDraftKeyRef = useRef(null);   // avoids rewriting an unchanged draft
  const suppressGuardRef = useRef(false); // intentional navigation (Google sign-in)

  const [draftOffer, setDraftOffer] = useState(null);
  const [cleanTick, setCleanTick] = useState(0);

  const hasUnsavedWork = useCallback(() => {
    if (baselineRef.current === null) return false;
    const { getSnapshot: snap, getWorkspace: ws } = latest.current;
    return shouldWarnBeforeDiscard({
      workspace: ws(),
      currentSnapshot: snap(),
      baselineFingerprint: baselineRef.current,
    });
  }, []);

  // Write (or clear) the draft right now.
  const persistDraft = useCallback(() => {
    if (!decidedRef.current) return;
    const storage = browserStorage();
    if (hasUnsavedWork()) {
      const draft = buildDraft(latest.current.getWorkspace());
      const key = serializeDraft({ ...draft, savedAt: 0 });
      if (ownsDraftRef.current && key === lastDraftKeyRef.current) return;
      if (saveDraft(storage, draft)) {
        ownsDraftRef.current = true;
        lastDraftKeyRef.current = key;
      }
    } else if (ownsDraftRef.current) {
      clearDraft(storage);
      ownsDraftRef.current = false;
      lastDraftKeyRef.current = null;
    }
  }, [hasUnsavedWork]);

  // Startup: take the baseline, then offer a stored draft (if worth it).
  useEffect(() => {
    baselineRef.current = workspaceFingerprint(latest.current.getSnapshot());
    const draft = loadDraft(browserStorage());
    if (draft && isDraftWorthRestoring(draft)) {
      setDraftOffer(draft);
    } else {
      decidedRef.current = true;
    }
  }, []);

  // markClean(): the workspace now matches what is persisted (or is fresh).
  // Taken in an effect so it sees the state AFTER React applies the
  // load/save/new updates that were batched with this call.
  const markClean = useCallback(() => setCleanTick((n) => n + 1), []);
  useEffect(() => {
    if (cleanTick === 0) return;
    baselineRef.current = workspaceFingerprint(latest.current.getSnapshot());
    persistDraft(); // clears this tab's draft now that nothing is unsaved
  }, [cleanTick, persistDraft]);

  // Debounced autosave after any render (cheap: skipped if unchanged).
  useEffect(() => {
    if (!decidedRef.current) return undefined;
    const t = setTimeout(persistDraft, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(t);
  });

  // Leave-page guard (reload, close, external navigation).
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (suppressGuardRef.current || !hasUnsavedWork()) return undefined;
      persistDraft();
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    // Coming back via the browser's Back button re-arms the guard.
    const onPageShow = () => {
      suppressGuardRef.current = false;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [hasUnsavedWork, persistDraft]);

  // For actions that would discard the whole workspace (New, Load Session).
  const confirmDiscard = useCallback(
    (message) => !hasUnsavedWork() || window.confirm(message),
    [hasUnsavedWork]
  );

  // Called just before the Google sign-in redirect.
  const prepareForRedirect = useCallback(() => {
    suppressGuardRef.current = true;
    persistDraft();
  }, [persistDraft]);

  const acceptDraft = useCallback(() => {
    if (!draftOffer) return;
    latest.current.onRestore(draftToWorkspace(draftOffer, latest.current.makeTabId));
    // Restored work is unsaved: the baseline stays at the empty startup
    // state, and the stored draft now belongs to this tab.
    ownsDraftRef.current = true;
    lastDraftKeyRef.current = null;
    decidedRef.current = true;
    setDraftOffer(null);
  }, [draftOffer]);

  const discardDraft = useCallback(() => {
    clearDraft(browserStorage());
    ownsDraftRef.current = false;
    lastDraftKeyRef.current = null;
    decidedRef.current = true;
    setDraftOffer(null);
  }, []);

  return {
    draftOffer,
    acceptDraft,
    discardDraft,
    hasUnsavedWork,
    confirmDiscard,
    markClean,
    prepareForRedirect,
  };
}
